import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
// Порт ref DesignBlockEditor.tsx:727-812 (case 'table'): строка 0 — шапка, полосатость
// и границы из blockTheme.designBlock, размер текста авто по числу столбцов.
// В правке ячейка — авто-растущая textarea без рамок, в read-only — текст, пустая → «—».
// Ширины колонок — по содержимому (`useTableColumns`), как в мобилке.
// Отличие от рефа: собственный onClick на носителе не вешаем — выделение элемента уже
// делает обёртка SortableDesignElement, textarea сама зовёт onSelect по фокусу.
import { messages } from './messages'
import { useState } from 'react'
import { useStrictLang } from './strictLangContext'
import { cn } from '@teach/ui'
import type { DesignTable, DesignTableCell } from '@/types/blocks'
import { getLocalizedDisplay, setLocalizedField } from '@/features/editor/lib/localizedField'
import { tableRows } from '@/features/editor/lib/designElements'
import { editTableCell, tableColumnCount } from '@/features/editor/lib/designTable'
import { TableCellContent } from './TableCellContent'
import { TableMeasure } from './TableMeasure'
import { useTableColumns } from './useTableColumns'
import { alignClass, backdropTextColor } from '@/features/editor/lib/designElementStyles'
import type { DesignElementProps } from './DesignElementView'
import {
  TABLE_CELL_CLASS,
  tableCellStyle,
  TABLE_FRAME_STYLE,
  tableRowStyle,
  tableTextSizeClass,
} from '@/features/editor/lib/tableStyles'

/** Ключ строки: id её первой ячейки — своего id у строки нет, а ячейки переживают
 *  добавление/удаление соседних строк и колонок. Пустая строка — только вырожденный
 *  случай битых данных, там падаем на индекс. */
const rowKey = (row: DesignTableCell[], index: number): string => row[0]?.id ?? `row-${index}`

export const TableElement = ({
  element,
  editable,
  contentLang,
  onUpdate,
  onSelect,
}: DesignElementProps<DesignTable>) => {
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const strictLang = useStrictLang()
  const [frame, setFrame] = useState<HTMLDivElement | null>(null)
  // Матрицы может не быть вовсе (битые данные бека) — иначе TypeError уносил
  // не таблицу, а всё телефон-превью.
  const data = tableRows(element.tableData)
  const rowCount = data.length
  const colCount = tableColumnCount(data)
  const sizeClass = tableTextSizeClass(colCount)
  const textClasses = cn(sizeClass, alignClass(element.textAlign))
  // Таблица своей подложки не имеет — тот же резолвер, что у TextElement/
  // HeadingElement (backdrop='none'), а не отдельное чтение
  // blockTheme.designBlock.text напрямую: один источник цвета текста вместо трёх.
  const color = backdropTextColor('none')
  // Симметричный фолбэк только в read-only, и «—» уже ПОСЛЕ него: пустой
  // перевод — это «не переведено», а не «пустая ячейка».
  const shown = data.map((row) =>
    row.map((cell) => getLocalizedDisplay(cell, 'content', contentLang, editable || strictLang)),
  )
  const placeholderOf = (ri: number) => (ri === 0 ? cm.heading : cm.cell)
  // Что меряем для ширины колонок — то, что видно: текст без краевых пробелов,
  // а у пустой ячейки — подсказку (в правке) или прочерк.
  const measured = shown.map((row, ri) =>
    row.map((text) => text.trim() || (editable ? placeholderOf(ri) : '—')),
  )
  const percents = useTableColumns(frame, measured)
  const widest = data.find((row) => row.length === colCount) ?? []

  // Правка одной ячейки: соседи остаются теми же объектами, лист другого языка
  // не трогается вовсе. Пересборку матрицы делает designTable — он же держит
  // её прямоугольной, если данные пришли рваными.
  const editCell = (rowIndex: number, colIndex: number, value: string): void =>
    onUpdate({
      tableData: editTableCell(
        data,
        rowIndex,
        colIndex,
        setLocalizedField('content', contentLang, value),
      ),
    })

  return (
    <div ref={setFrame} className="relative w-full overflow-hidden" style={TABLE_FRAME_STYLE}>
      <TableMeasure texts={measured} sizeClass={sizeClass} />
      {/* Замер есть — раскладка фиксированная по нему; до замера — HTML-авто. */}
      <table className={cn('w-full', percents ? 'table-fixed' : null)}>
        {percents ? (
          <colgroup>
            {percents.map((width, ci) => (
              <col key={widest[ci]?.id ?? `col-${ci}`} style={{ width: `${width}%` }} />
            ))}
          </colgroup>
        ) : null}
        <tbody>
          {data.map((row, ri) => (
            <tr key={rowKey(row, ri)} style={tableRowStyle(ri)}>
              {row.map((cell, ci) => (
                <td
                  key={cell.id}
                  className={cn(TABLE_CELL_CLASS, ri === 0 ? 'font-bold' : null)}
                  style={tableCellStyle({ row: ri, rowCount, col: ci, colCount: row.length })}
                >
                  <TableCellContent
                    editable={editable}
                    value={shown[ri][ci]}
                    placeholder={placeholderOf(ri)}
                    className={textClasses}
                    color={color}
                    onChange={(value) => editCell(ri, ci, value)}
                    onFocus={onSelect}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
